import { useMemo, useState } from "react";

import { useUser } from "@components/user-context";
import { useCustomMetrics } from "@features/custom-metrics/hooks/use-custom-metrics";
import { DataExportBuilderModal } from "@features/data-export";
import { useShowV1Forecasts } from "@features/sales-v3/hooks/use-show-v1-forecasts";
import { FETCH_CUSTOM_GOALS } from "@gql/creative-analytics";
import { useFeatureFlags, useNorthbeamQuery } from "@hooks/http";
import { type FetchCustomGoals } from "@north-beam/nb-client-side";
import { getSalesPageMetricSpecs } from "@north-beam/nb-client-side/metrics";
import { SecondaryButton } from "@shared/buttons";

type CreateScheduledExportButtonWithModalProps = {
  onSuccess?(): Promise<void>;
};

export const CreateScheduledExportButtonWithModal = ({
  onSuccess,
}: CreateScheduledExportButtonWithModalProps) => {
  const { user } = useUser();
  const { hasFeatureFlag } = useFeatureFlags();

  const [isExportModalOpen, setIsExportModalOpen] = useState(false);
  const canCreateExports = hasFeatureFlag(
    "enableExportCreationOnManageExportPage",
  );

  const { data: customGoalsData, loading } =
    useNorthbeamQuery<FetchCustomGoals>(FETCH_CUSTOM_GOALS);

  const customGoals = useMemo(
    () => customGoalsData?.me.customGoals.goals ?? [],
    [customGoalsData?.me.customGoals],
  );
  const { customMetricData } = useCustomMetrics();

  const showV1Forecasts = useShowV1Forecasts(
    "daily",
    "northbeam_custom",
    "accrual",
  );

  const metricSpecs = useMemo(() => {
    return getSalesPageMetricSpecs(
      user,
      showV1Forecasts,
      customGoals,
      customMetricData,
    );
  }, [customMetricData, customGoals, showV1Forecasts, user]);

  if (loading) {
    return <p>Please wait...</p>;
  }

  if (!canCreateExports || !metricSpecs) {
    return null;
  }

  return (
    <>
      <DataExportBuilderModal
        isOpen={isExportModalOpen}
        hideExportNowButton={true}
        target="performance-metrics"
        availableMetricSpecs={metricSpecs}
        selectedMetricIds={[
          "rev",
          "txns",
          "revFt",
          "txnsFt",
          "revRtn",
          "txnsRtn",
          "visits",
          "impressions",
          "roas",
          "cac",
        ]}
        attributionModel="northbeam_custom"
        attributionWindow="1"
        nbAccountingMode="accrual"
        granularity="platform"
        timeGranularity="daily"
        breakdowns={[]}
        dateRange="last7Days"
        onClose={() => {
          setIsExportModalOpen(false);
        }}
        onAfterSave={onSuccess}
      />
      <SecondaryButton
        className="font-bold"
        onClick={() => setIsExportModalOpen(true)}
      >
        CREATE SCHEDULED EXPORT
      </SecondaryButton>
    </>
  );
};
